import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { authService } from '@/services/auth.service';
import { AuthResponse, AuthState, AuthUser } from '@/types/auth';
import { Provider } from '@/types/tenant';
import { coerceAuthUser, coerceProvider } from '@/utils/auth-session';
import { computeProviderDomain, setProviderDomain, setProviderSlug } from '@/lib/provider-context';
import { getCurrentDomain, getDevProviderSlug } from '@/utils/domain';

type CustomerAuthState = AuthState;

interface CustomerAuthContextValue extends CustomerAuthState {
  checkAuth: () => Promise<void>;
  establishSession: (user: AuthUser, provider: Provider, token: string) => Promise<void>;
  hydrateFromResponse: (response: AuthResponse | AuthResponse['data']) => Promise<void>;
  logout: () => Promise<void>;
  hasRole: (role: string | string[]) => boolean;
}

const CustomerAuthContext = createContext<CustomerAuthContextValue | undefined>(undefined);

interface CustomerAuthProviderProps {
  children: ReactNode;
}

const CUSTOMER_SESSION_STORAGE_KEY = 'florentin.customer.session';

interface PersistedCustomerSession {
  user: AuthUser;
  provider: Provider;
  timestamp: number;
}

const initialState: CustomerAuthState = {
  user: null,
  provider: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

export function CustomerAuthProvider({ children }: CustomerAuthProviderProps) {
  const [state, setState] = useState<CustomerAuthState>(initialState);

  const establishSession = useCallback(async (user: AuthUser, provider: Provider, token: string) => {
    setState({
      user,
      provider,
      token,
      isAuthenticated: true,
      isLoading: false,
      error: null,
    });
    const resolvedDomain = computeProviderDomain({
      provider,
      fallbackSlug: provider?.slug ?? getDevProviderSlug(),
      fallbackHostname: getCurrentDomain().hostname,
    });
    setProviderDomain(resolvedDomain);
    const resolvedSlug = provider?.slug ?? getDevProviderSlug();
    setProviderSlug(resolvedSlug);
    persistCustomerSessionSnapshot(user, provider);
  }, []);

  const checkAuth = useCallback(async () => {
    const token = authService.getAuthToken('customer');

    if (!token) {
      clearPersistedCustomerSession();
      setState({
        ...initialState,
        isLoading: false,
      });
      return;
    }

    const cachedSession = getPersistedCustomerSession();
    if (cachedSession) {
      setState({
        user: cachedSession.user,
        provider: cachedSession.provider,
        token,
        isAuthenticated: true,
        isLoading: true,
        error: null,
      });
    } else {
      setState((previous) => ({ ...previous, isLoading: true, error: null }));
    }

    try {
      const response = await authService.getCurrentCustomer();
      const { user, provider } = normalizeCustomerSessionPayload(response);

      if (!user) {
        throw new Error('No customer user returned from API');
      }

      persistCustomerSessionSnapshot(user, provider);

      setState({
        user,
        provider,
        token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      const resolvedDomain = computeProviderDomain({
        provider,
        fallbackSlug: provider?.slug ?? getDevProviderSlug(),
        fallbackHostname: getCurrentDomain().hostname,
      });
      setProviderDomain(resolvedDomain);
      const resolvedSlug = provider?.slug ?? getDevProviderSlug();
      setProviderSlug(resolvedSlug);
    } catch (error) {
      console.error('Failed to hydrate customer session:', error);
      const status = getStatusFromError(error);

      if (status === 401) {
        authService.clearAuthToken('customer');
        clearPersistedCustomerSession();
        setState({
          ...initialState,
          isLoading: false,
          error: 'Customer session expired, please sign in again.',
        });
        return;
      }

      if (cachedSession) {
        setState({
          user: cachedSession.user,
          provider: cachedSession.provider,
          token,
          isAuthenticated: true,
          isLoading: false,
          error: 'Unable to refresh your session. Showing cached data.',
        });
        return;
      }

      setState({
        ...initialState,
        isLoading: false,
        error: 'Unable to restore your session. Please sign in again.',
      });
    }
  }, []);

  const hydrateFromResponse = useCallback(
    async (response: AuthResponse | AuthResponse['data']) => {
      const authData = isAuthPayload(response) ? response : response.data;

      if (!authData || !authData.user || !authData.token) {
        throw new Error('Invalid authentication response for customer');
      }

      const meta =
        authData.meta ??
        (isFullAuthResponse(response) ? response.meta ?? authData.meta : authData.meta);
      const awaitingOnboarding =
        Boolean(meta?.is_new_user) && typeof meta?.onboarding_user_id === 'string';

      await establishSession(authData.user, authData.provider, authData.token);

      const listRoles = Array.isArray(authData.user.roles) ? authData.user.roles : [];
      const primaryRole = typeof authData.user.role === 'string' ? authData.user.role : undefined;
      const normalizedRoles = [primaryRole, ...listRoles]
        .filter((role): role is string => typeof role === 'string' && role.length > 0)
        .map((role) => canonicalizeRole(role));
      const ownerAliases = ROLE_EQUIVALENTS.customer_owner;
      const hasOwnerRole = normalizedRoles.some((role) => ownerAliases.includes(role));

      if (!hasOwnerRole && !awaitingOnboarding) {
        void checkAuth();
      }
    },
    [establishSession, checkAuth]
  );

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    setState((previous) => ({ ...previous, isLoading: true }));

    try {
      await authService.logoutCustomer();
    } catch (error) {
      console.error('Customer logout failed:', error);
    }

    authService.clearAuthToken('customer');
    clearPersistedCustomerSession();
    setState({ ...initialState, isLoading: false });
  }, []);

  const hasRole = useCallback(
    (roleOrRoles: string | string[]) => {
      const userRoles = state.user?.roles ?? [];
      const primaryRole = state.user?.role;
      const seenRoles = new Set<string>();

      const registerRole = (role?: string | null) => {
        if (!role) {
          return;
        }

        const key = canonicalizeRole(role);
        const variants = ROLE_EQUIVALENTS[key] ?? [key];
        variants.forEach((variant) => {
          seenRoles.add(variant);
        });
      };

      userRoles.forEach(registerRole);
      registerRole(primaryRole ?? null);

      const requestedRoles = Array.isArray(roleOrRoles) ? roleOrRoles : [roleOrRoles];

      return requestedRoles.some((candidate) => {
        const key = canonicalizeRole(candidate);
        if (seenRoles.has(key)) {
          return true;
        }

        const variants = ROLE_EQUIVALENTS[key];
        return variants ? variants.some((variant) => seenRoles.has(variant)) : false;
      });
    },
    [state.user?.roles, state.user?.role]
  );

  const value = useMemo<CustomerAuthContextValue>(
    () => ({
      ...state,
      checkAuth,
      establishSession,
      hydrateFromResponse,
      logout,
      hasRole,
    }),
    [state, checkAuth, establishSession, hydrateFromResponse, logout, hasRole]
  );

  return <CustomerAuthContext.Provider value={value}>{children}</CustomerAuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useCustomerAuth(): CustomerAuthContextValue {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }
  return context;
}

 
function isAuthPayload(value: unknown): value is AuthResponse['data'] {
  return (
    typeof value === 'object' &&
    value !== null &&
    'user' in value &&
    'provider' in value &&
    'token' in value
  );
}

function isFullAuthResponse(value: unknown): value is AuthResponse {
  return (
    typeof value === 'object' &&
    value !== null &&
    'data' in value &&
    'message' in value
  );
}

function normalizeCustomerSessionPayload(
  payload: unknown
): { user: AuthUser | null; provider: Provider | null } {
  const directSession = extractSessionFromValue(payload);
  if (directSession) {
    return directSession;
  }

  if (typeof payload === 'object' && payload !== null) {
    const record = payload as Record<string, unknown>;

    if ('data' in record) {
      const nested = normalizeCustomerSessionPayload(record.data);
      if (nested.user || nested.provider) {
        return nested;
      }
    }

    if ('meta' in record) {
      const nested = normalizeCustomerSessionPayload(record.meta);
      if (nested.user || nested.provider) {
        return nested;
      }
    }
  }

  return { user: null, provider: null };
}

function persistCustomerSessionSnapshot(user: AuthUser | null, provider: Provider | null): void {
  if (typeof window === 'undefined') {
    return;
  }

  if (!user || !provider) {
    clearPersistedCustomerSession();
    return;
  }

  try {
    const payload: PersistedCustomerSession = {
      user,
      provider,
      timestamp: Date.now(),
    };
    window.localStorage.setItem(CUSTOMER_SESSION_STORAGE_KEY, JSON.stringify(payload));
  } catch (storageError) {
    console.warn('Failed to persist customer session snapshot', storageError);
  }
}

function getPersistedCustomerSession(): PersistedCustomerSession | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const raw = window.localStorage.getItem(CUSTOMER_SESSION_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<PersistedCustomerSession>;
    const user = coerceAuthUser(parsed.user);
    const provider = coerceProvider(parsed.provider);
    if (!user || !provider) {
      return null;
    }
    return {
      user,
      provider,
      timestamp: typeof parsed.timestamp === 'number' ? parsed.timestamp : Date.now(),
    };
  } catch {
    return null;
  }
}

function clearPersistedCustomerSession(): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.removeItem(CUSTOMER_SESSION_STORAGE_KEY);
}

function getStatusFromError(error: unknown): number | null {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const directStatus = (error as { status?: unknown }).status;
  if (typeof directStatus === 'number') {
    return directStatus;
  }

  if ('response' in error && error.response && typeof error.response === 'object') {
    const nestedStatus = (error.response as { status?: unknown }).status;
    if (typeof nestedStatus === 'number') {
      return nestedStatus;
    }
  }

  return null;
}

function extractSessionFromValue(value: unknown): { user: AuthUser | null; provider: Provider | null } | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }

  const record = value as Record<string, unknown>;
  const user = coerceAuthUser(record.customer ?? record.user);
  const provider = coerceProvider(record.provider);

  if (!user && !provider) {
    return null;
  }

  return {
    user,
    provider,
  };
}

const ROLE_EQUIVALENTS: Record<string, readonly string[]> = {
  customer_owner: ['customer_owner', 'owner', 'team_owner', 'workspace_owner'],
  owner: ['customer_owner', 'owner', 'team_owner', 'workspace_owner'],
  team_owner: ['customer_owner', 'owner', 'team_owner', 'workspace_owner'],
  workspace_owner: ['customer_owner', 'owner', 'team_owner', 'workspace_owner'],
  customer_admin: ['customer_admin', 'admin', 'team_admin', 'workspace_admin'],
  admin: ['customer_admin', 'admin', 'team_admin', 'workspace_admin'],
  team_admin: ['customer_admin', 'admin', 'team_admin', 'workspace_admin'],
  workspace_admin: ['customer_admin', 'admin', 'team_admin', 'workspace_admin'],
  customer_member: ['customer_member', 'member', 'workspace_member', 'team_member'],
  member: ['customer_member', 'member', 'workspace_member', 'team_member'],
  workspace_member: ['customer_member', 'member', 'workspace_member', 'team_member'],
  team_member: ['customer_member', 'member', 'workspace_member', 'team_member'],
  customer_individual: ['customer_individual', 'individual'],
  individual: ['customer_individual', 'individual'],
};

const canonicalizeRole = (role: string): string => role.trim().toLowerCase();
